import React, { useMemo, useState, useCallback } from "react";
import { formatCurrency } from "@/shared/utils/formatters";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Check } from "lucide-react";
import MermaidRenderer from "@/shared/components/ai/MermaidRenderer";
import { LimiteMensagemChat } from "@/interno/instituicoes/componentes/LimiteMensagemChat";

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "#8b5cf6",
  "#f59e0b",
  "#10b981",
];

/** Parse a ```chart block into structured data */
function parseChartBlock(code: string): {
  tipo: string;
  titulo: string;
  dados: { label: string; valor: number }[];
} | null {
  try {
    const lines = code.trim().split("\n");
    let tipo = "barra";
    let titulo = "";
    const dados: { label: string; valor: number }[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith("tipo:")) {
        tipo = trimmed.replace("tipo:", "").trim().toLowerCase();
      } else if (trimmed.startsWith("titulo:")) {
        titulo = trimmed.replace("titulo:", "").trim();
      } else if (trimmed.startsWith("- label:")) {
        const match = trimmed.match(/- label:\s*(.+?)\s*\|\s*valor:\s*([\d.,]+)/);
        if (match) {
          dados.push({
            label: match[1].trim(),
            valor: parseFloat(match[2].replace(/\./g, "").replace(",", ".")),
          });
        }
      }
    }

    if (dados.length === 0) return null;
    return { tipo, titulo, dados };
  } catch {
    return null;
  }
}

function ChartRenderer({ code }: { code: string }) {
  const chart = useMemo(() => parseChartBlock(code), [code]);
  if (!chart) return null;

  const { tipo, titulo, dados } = chart;

  return (
    <div className="my-4 p-4 bg-card border rounded-lg">
      {titulo && (
        <h4 className="text-sm font-semibold text-foreground mb-3 text-center">{titulo}</h4>
      )}
      <ResponsiveContainer width="100%" height={280}>
        {tipo === "pizza" ? (
          <PieChart>
            <Pie
              data={dados}
              dataKey="valor"
              nameKey="label"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={({ label, percent }) => `${label} (${(percent * 100).toFixed(0)}%)`}
            >
              {dados.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => formatCurrency(value)} />
            <Legend />
          </PieChart>
        ) : tipo === "linha" ? (
          <LineChart data={dados}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value: number) => formatCurrency(value)} />
            <Line
              type="monotone"
              dataKey="valor"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={{ fill: "hsl(var(--primary))" }}
            />
          </LineChart>
        ) : (
          <BarChart data={dados}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value: number) => formatCurrency(value)} />
            <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
              {dados.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

/** Interactive Checklist Item */
function ChecklistItem({ children, defaultChecked = false }: { children: React.ReactNode; defaultChecked?: boolean }) {
  const [checked, setChecked] = useState(defaultChecked);

  return (
    <li className="flex items-start gap-2 py-1 list-none">
      <button
        type="button"
        onClick={() => setChecked(!checked)}
        className={`mt-0.5 shrink-0 h-5 w-5 rounded border-2 flex items-center justify-center transition-all ${
          checked
            ? "bg-emerald-500 border-emerald-500 text-white"
            : "border-muted-foreground/40 hover:border-primary"
        }`}
      >
        {checked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      </button>
      <span className={`text-sm ${checked ? "line-through text-muted-foreground" : "text-foreground"}`}>
        {children}
      </span>
    </li>
  );
}

/** Split markdown into text segments, chart blocks and mermaid blocks */
function splitContent(markdown: string): { type: "text" | "chart" | "mermaid"; content: string }[] {
  const parts: { type: "text" | "chart" | "mermaid"; content: string }[] = [];
  const regex = /```(chart|mermaid)\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(markdown)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: markdown.slice(lastIndex, match.index) });
    }
    parts.push({ type: match[1] as "chart" | "mermaid", content: match[2] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < markdown.length) {
    parts.push({ type: "text", content: markdown.slice(lastIndex) });
  }

  return parts;
}

const markdownComponents = {
  table: ({ children, ...props }: any) => (
    <div className="overflow-x-auto my-3 max-w-full w-full">
      <table className="w-full min-w-0 border-collapse text-sm" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }: any) => (
    <thead className="bg-muted/60" {...props}>{children}</thead>
  ),
  th: ({ children, ...props }: any) => (
    <th className="border border-border px-3 py-2 text-left font-semibold text-foreground text-xs" {...props}>
      {children}
    </th>
  ),
  td: ({ children, ...props }: any) => (
    <td className="border border-border px-3 py-2 text-foreground text-xs" {...props}>
      {children}
    </td>
  ),
  tr: ({ children, ...props }: any) => (
    <tr className="even:bg-muted/30" {...props}>{children}</tr>
  ),
  li: ({ children, node, ...props }: any) => {
    // Detect checklist items: - [ ] or - [x]
    const firstChild = node?.children?.[0];
    if (firstChild?.type === "element" && firstChild?.tagName === "input" && firstChild?.properties?.type === "checkbox") {
      const isChecked = firstChild.properties.checked === true;
      // Filter out the input element from children rendering
      const textContent = node?.children?.slice(1);
      return (
        <ChecklistItem defaultChecked={isChecked}>
          {children}
        </ChecklistItem>
      );
    }
    return <li {...props}>{children}</li>;
  },
  input: ({ node, ...props }: any) => {
    // Hide the default checkbox input since we render our own
    if (props.type === "checkbox") return null;
    return <input {...props} />;
  },
};

/** Detecta se texto parece ser path R2 (ex: pasta/subpasta/arquivo.pdf) */
function parecePathR2(texto: string): boolean {
  const t = String(texto).trim();
  if (!t || t.includes("\n")) return false;
  return /^[\w\-. /\\]+$/.test(t) && t.includes("/");
}

/** Extrai diretório e nome de arquivo de uma key R2 */
function extrairPathENome(key: string): { path: string; nome: string } {
  const k = key.replace(/\/$/, "");
  const lastSlash = k.lastIndexOf("/");
  if (lastSlash < 0) return { path: "", nome: k };
  return { path: k.slice(0, lastSlash + 1), nome: k.slice(lastSlash + 1) };
}

interface AiMarkdownRendererProps {
  content: string;
  className?: string;
  /** Callback ao clicar em arquivo (key R2) */
  onFileClick?: (key: string, nome: string) => void;
  /** Callback ao clicar em path (diretório R2) */
  onPathClick?: (path: string) => void;
}

/** Fallback para blocos que falham (chart, mermaid, markdown) */
function RenderFallback({ type }: { type: string }) {
  return (
    <div className="my-2 rounded border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
      Não foi possível renderizar este {type}.
    </div>
  );
}

const AiMarkdownRenderer: React.FC<AiMarkdownRendererProps> = ({
  content,
  className = "",
  onFileClick,
  onPathClick,
}) => {
  const parts = useMemo(() => {
    try {
      return splitContent(content);
    } catch {
      return [{ type: "text" as const, content }];
    }
  }, [content]);

  const components = useMemo(() => {
    const base = { ...markdownComponents };
    if (onFileClick || onPathClick) {
      base.code = ({ children, node, ...props }: any) => {
        const raw = String(children ?? "").trim();
        const isInline = !node?.properties?.className; // block code tem language-xxx
        if (isInline && parecePathR2(raw)) {
          const terminaComBarra = raw.endsWith("/");
          if (terminaComBarra && onPathClick) {
            return (
              <button
                type="button"
                onClick={() => onPathClick(raw)}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#d7b369]/20 text-[#791614] hover:bg-[#d7b369]/35 text-xs font-mono cursor-pointer border border-[#d7b369]/40"
                title="Ir ao diretório"
              >
                {raw}
              </button>
            );
          }
          if (!terminaComBarra && (onFileClick || onPathClick)) {
            const { path, nome } = extrairPathENome(raw);
            return (
              <span className="inline-flex flex-wrap items-center gap-1">
                {onFileClick && nome && (
                  <button
                    type="button"
                    onClick={() => onFileClick(raw, nome)}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#791614]/10 text-[#791614] hover:bg-[#791614]/20 text-xs font-mono cursor-pointer border border-[#791614]/30"
                    title="Abrir arquivo"
                  >
                    {nome}
                  </button>
                )}
                {onPathClick && path && (
                  <button
                    type="button"
                    onClick={() => onPathClick(path)}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#d7b369]/20 text-[#791614] hover:bg-[#d7b369]/35 text-xs font-mono cursor-pointer border border-[#d7b369]/40"
                    title="Ir ao diretório"
                  >
                    {path}
                  </button>
                )}
              </span>
            );
          }
        }
        return <code {...props}>{children}</code>;
      };
    }
    return base;
  }, [onFileClick, onPathClick]);

  return (
    <div className={`prose prose-sm max-w-none break-words text-foreground dark:prose-invert ${className}`}>
      {parts.map((part, i) => (
        <LimiteMensagemChat
          key={i}
          fallback={<RenderFallback type={part.type === "chart" ? "gráfico" : part.type === "mermaid" ? "diagrama" : "texto"} />}
        >
          {part.type === "chart" ? (
            <ChartRenderer code={part.content} />
          ) : part.type === "mermaid" ? (
            <MermaidRenderer code={part.content} className="my-4" />
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {part.content}
            </ReactMarkdown>
          )}
        </LimiteMensagemChat>
      ))}
    </div>
  );
};

export default AiMarkdownRenderer;
